// components/JoinNowFormPopup.tsx

import React, { useState } from 'react';

// Define the props that this component will accept
interface JoinNowFormPopupProps {
  onClose: () => void; // Function to close the popup
  courseTitle: string; // The title of the course the user is interested in
}

const JoinNowFormPopup: React.FC<JoinNowFormPopupProps> = ({ onClose, courseTitle }) => {
  // State to manage the form input values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: `I'm interested in the "${courseTitle}" course.`, // Pre-filled message
  });

  // State for submission status (optional, but good for user feedback)
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); // New state for submission status

  // Handle changes in form inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default browser form submission
    setIsSubmitting(true); // Disable button
    setSubmissionStatus('Submitting inquiry...'); // Set submitting status

    // Prepare data for the backend, mapping to FormSubmissionDto fields
    const dataToSend = {
      firstName: formData.name, // Map name to firstName
      lastName: '', // Leave empty or set to a default if not collected
      businessEmail: formData.email,
      phone: formData.phone,
      companyName: 'N/A', // Set a default value or collect if needed
      interests: [`Course Inquiry: ${courseTitle}`], // Indicate interest in the course
      howDidYouFindUs: 'Website Join Now Form', // Set a default value
      subscribeNewsletter: false, // Default to false, or add a checkbox if needed
      message: formData.message, // Map message directly
    };

    try {
      // Send data to your backend API
      const response = await fetch('https://www.nadezhda.in/api/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (response.ok) {
        setSubmissionStatus('Thank you for your inquiry! We will get back to you soon.');
        // Optionally clear the form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: `I'm interested in the "${courseTitle}" course.`,
        });
        setTimeout(() => onClose(), 2000); // Close after a short delay to show success message
      } else {
        const errorText = await response.text();
        setSubmissionStatus(`Submission failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmissionStatus('An error occurred during submission.');
    } finally {
      setIsSubmitting(false); // Re-enable button
    }
  };

  return (
    // Overlay for the pop-up, covering the entire screen
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {/* Pop-up container - Changed background to light blue, adjusted shadow for 3D effect */}
      <div className="bg-blue-50 p-8 rounded-xl shadow-2xl max-w-md w-full relative border border-blue-200">
        {/* Close button - Adjusted color for better contrast with new background */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-blue-600 hover:text-blue-800 text-2xl"
          aria-label="Close"
        >
          &times; {/* This is the 'x' character for closing */}
        </button>

        {/* Pop-up title, dynamically showing the course name - Adjusted color */}
        <h2 className="text-2xl font-bold mb-6 text-center text-blue-800">
          Enroll in {courseTitle}
        </h2>

        {/* The form itself */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name Field - Adjusted border, focus ring, and placeholder color */}
          <div>
            <label htmlFor="name" className="block text-blue-700 text-sm font-medium mb-1">
              Name:
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full p-3 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-900 placeholder-blue-400 shadow-sm"
              placeholder="Your Full Name"
              required // HTML5 validation for required field
            />
          </div>

          {/* Email Field - Adjusted border, focus ring, and placeholder color */}
          <div>
            <label htmlFor="email" className="block text-blue-700 text-sm font-medium mb-1">
              Email:
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-3 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-900 placeholder-blue-400 shadow-sm"
              placeholder="your.email@example.com"
              required
            />
          </div>

          {/* Phone Number Field - Adjusted border, focus ring, and placeholder color */}
          <div>
            <label htmlFor="phone" className="block text-blue-700 text-sm font-medium mb-1">
              Phone Number:
            </label>
            <input
              type="tel" // Use type="tel" for phone numbers
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full p-3 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-900 placeholder-blue-400 shadow-sm"
              placeholder="e.g., +91 9876543210"
              required
            />
          </div>

          {/* Message Textarea - Adjusted border, focus ring, and placeholder color */}
          <div>
            <label htmlFor="message" className="block text-blue-700 text-sm font-medium mb-1">
              Message:
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={4} // Number of visible text lines
              className="w-full p-3 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-900 placeholder-blue-400 resize-y shadow-sm"
              placeholder="Tell us more about your interest..."
            ></textarea>
          </div>

          {/* Submit Button - Changed background to a more pronounced blue, added subtle shadow */}
          <div className="flex items-center justify-between">
            <button
              type="submit"
              className={`w-full py-3 px-4 rounded-md text-white font-semibold transition-colors duration-300 shadow-md hover:shadow-lg
                ${isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting Inquiry...' : 'Submit Inquiry'}
            </button>
          </div>
          {submissionStatus && (
            <p className={`mt-3 text-center text-sm ${submissionStatus.includes('failed') || submissionStatus.includes('error') ? 'text-red-600' : 'text-blue-600'}`}>
              {submissionStatus}
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default JoinNowFormPopup;